import classnames from 'classnames';
import styled from 'styled-components';

import { css } from '@sorare/css';

import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { laptopAndBelow } from '#src/style/mediaQuery.ts';
import { theme } from '#src/style/theme.tsx';

import minus18Icon from './assets/18.svg';
import franceLogo from './assets/france.webp';

const JOUEURS_INFO_SERVICE_URL = 'https://www.joueurs-info-service.fr';

const Root = styled.a`
  display: flex;
  background: var(--c-surface-opaque);
  isolation: isolate;
  flex-wrap: wrap;
  text-decoration: none;
  padding: var(--double-unit) var(--quadruple-unit);
  gap: var(--double-unit);
  justify-content: center;
  align-items: center;
  z-index: ${theme.zIndex.snackbar + 1};
`;

const Message = styled.p`
  display: flex;
  flex-direction: column;
  gap: var(--half-unit);
  font: var(--t-16);
  font-weight: 700;
  text-transform: uppercase;
  color: var(--c-onBase-primary);
  text-align: center;
  line-height: 1.2;
  margin: 0;
  max-width: 1000px;
`;

const SecondaryLine = styled.span`
  font: var(--t-14);
  font-weight: 700;
`;

const compactRoot = css`
  @media ${laptopAndBelow} {
    padding: var(--unit) var(--double-unit);
    gap: var(--unit);
  }
`;

const compactMessage = css`
  @media ${laptopAndBelow} {
    font: var(--t-14-16);
    font-weight: 700;
  }
`;

const compactSecondaryLine = css`
  @media ${laptopAndBelow} {
    font: var(--t-12);
    font-weight: 700;
  }
`;

const GouvernementLogoWrapper = styled.div`
  background: white;
  border-radius: var(--half-unit);
  padding: var(--half-unit) var(--unit);
  flex-grow: 0;
`;

type Props = {
  className?: string;
  compactOnMobile?: boolean;
};

export const LegalWarningBanner = ({
  className,
  compactOnMobile = false,
}: Props) => {
  const {
    currentLocation: { countryCode },
  } = useConfigContext();

  if (countryCode?.toUpperCase() !== 'FR') {
    return null;
  }

  return (
    <Root
      className={classnames(className, { [compactRoot]: compactOnMobile })}
      href={JOUEURS_INFO_SERVICE_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      <Message className={classnames({ [compactMessage]: compactOnMobile })}>
        <span>
          Votre activité de jeu peut être dangereuse : pertes d&apos;argent,
          conflits familiaux, addiction, etc.
        </span>
        <SecondaryLine
          className={classnames({ [compactSecondaryLine]: compactOnMobile })}
        >
          Retrouvez nos conseils sur joueurs-info-service.fr (09 74 75 13 13 -
          appel non surtaxé)
        </SecondaryLine>
      </Message>
      <Horizontal center gap={2}>
        <img src={minus18Icon} alt="Interdit aux moins de 18 ans" height={36} />
        <GouvernementLogoWrapper>
          <img
            src={franceLogo}
            alt="Gouvernement Français - Liberté, Égalité, Fraternité"
            height={36}
          />
        </GouvernementLogoWrapper>
      </Horizontal>
    </Root>
  );
};
